import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { usePaginationState } from '../../../hooks/usePaginationState';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";
import { Tooltip, Switch } from "@mui/material";
import { useDispatch } from "react-redux";
import { setShowMessage } from "../../../redux/slices/notificationSlice";
import AlertDeleteModal from "../../AlertDelete/AlertDeleteModal";
import ServicingApi from "../../../services/AssociatedServices";
import PersistentDataGrid from "../../table/PersistentDataGrid";

export default function ServiceList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const servicingApi = useMemo(() => new ServicingApi(), []);
  const [list, setList] = useState([]);

  // remember pagination state across navigation (sessionStorage)
  const [paginationModel, handlePaginationModelChange] = usePaginationState('serviceListPaginationState', { pageSize: 50, page: 0 });
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    hours_threshold: "",
    days_threshold: "",
  });

  const redirectBack = () => navigate("/administration/configuration");

  const loadList = async () => {
    try {
      const res = await servicingApi.getServicingList();
      // API returns response.data (body) directly via requestToken
      if (res?.isSuccess && Array.isArray(res.result)) {
        setList(res.result);
      } else if (res && !res.isSuccess) {
        dispatch(setShowMessage(res.message || "Failed to load service list"));
      }
    } catch (err) {
      console.error(err);
      dispatch(setShowMessage(err?.response?.data?.message || "Failed to load service list"));
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const handleAdd = () => {
    setEditId(null);
    setFormData({
      name: "",
      description: "",
      hours_threshold: "",
      days_threshold: "",
    });
    setModalShow(true);
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    setFormData({
      name: row.name || "",
      description: row.description || "",
      hours_threshold: row.hours_threshold != null ? String(row.hours_threshold) : "",
      days_threshold: row.days_threshold != null ? String(row.days_threshold) : "",
    });
    setModalShow(true);
  };

  const handleCloseModal = () => {
    setModalShow(false);
    setEditId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = formData.name.trim();
    const hours = formData.hours_threshold.trim();
    if (!name) {
      dispatch(setShowMessage("Name is required"));
      return;
    }
    if (!hours || isNaN(Number(hours))) {
      dispatch(setShowMessage("Hours threshold must be a number"));
      return;
    }
    const payload = {
      name,
      description: formData.description.trim() || null,
      hours_threshold: Number(hours),
      days_threshold: formData.days_threshold.trim() ? Number(formData.days_threshold) : null,
    };
    try {
      if (editId) {
        const res = await servicingApi.updateServiceListItem(editId, payload);
        if (res?.isSuccess) {
          dispatch(setShowMessage("Service updated"));
          handleCloseModal();
          loadList();
        } else {
          dispatch(setShowMessage(res?.message || "Update failed"));
        }
      } else {
        const res = await servicingApi.createServiceListItem(payload);
        if (res?.isSuccess) {
          dispatch(setShowMessage("Service added"));
          handleCloseModal();
          loadList();
        } else {
          dispatch(setShowMessage(res?.message || "Add failed"));
        }
      }
    } catch (err) {
      console.error(err);
      dispatch(setShowMessage(err?.response?.data?.message || "Request failed"));
    }
  };

  const deleteAction = (row) => {
    setDeleteId(row.id);
    setDeleteMessage(`Are you sure you want to delete "${row.name || "this service"}"?`);
    setDisplayConfirmationModal(true);
  };

  const hideConfirmationModal = () => {
    setDisplayConfirmationModal(false);
    setDeleteId(null);
  };

  const submitDelete = async (type, id) => {
    const idToDelete = id != null ? id : deleteId;
    if (!idToDelete) return;
    try {
      const res = await servicingApi.deleteServiceListItem(idToDelete);
      if (res?.isSuccess) {
        dispatch(setShowMessage("Service deleted"));
        hideConfirmationModal();
        loadList();
      } else {
        dispatch(setShowMessage(res?.message || "Delete failed"));
      }
    } catch (err) {
      console.error(err);
      dispatch(setShowMessage(err?.response?.data?.message || "Delete failed"));
    }
  };

  const handleToggleActive = async (row) => {
    const newActive = row.is_active === 1 || row.is_active === "1" ? 0 : 1;
    try {
      const res = await servicingApi.toggleServiceListActive(row.id, newActive);
      if (res?.isSuccess) {
        dispatch(setShowMessage(newActive ? "Marked active" : "Marked inactive"));
        loadList();
      } else {
        dispatch(setShowMessage(res?.message || "Update failed"));
      }
    } catch (err) {
      console.error(err);
      dispatch(setShowMessage(err?.response?.data?.message || "Update failed"));
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "No",
      headerAlign: "center",
      align: "center",
      minWidth: 70,
      flex: 0.5,
    },
    {
      field: "name",
      headerName: "Name",
      headerAlign: "left",
      align: "left",
      minWidth: 180,
      flex: 1,
      valueGetter: (params) => params.row.name ?? "-",
    },
    {
      field: "description",
      headerName: "Description",
      headerAlign: "left",
      align: "left",
      minWidth: 160,
      flex: 1,
      valueGetter: (params) => params.row.description ?? "-",
    },
    {
      field: "hours_threshold",
      headerName: "Hours",
      headerAlign: "center",
      align: "center",
      minWidth: 100,
      flex: 0.6,
      valueGetter: (params) => params.row.hours_threshold ?? "-",
    },
    {
      field: "days_threshold",
      headerName: "Days",
      headerAlign: "center",
      align: "center",
      minWidth: 90,
      flex: 0.5,
      valueGetter: (params) =>
        params.row.days_threshold != null ? params.row.days_threshold : "-",
    },
    {
      field: "is_active",
      headerName: "Active",
      headerAlign: "center",
      align: "center",
      minWidth: 90,
      flex: 0.5,
      renderCell: (params) => {
        if (params.row.hasOwnProperty("is_active")) {
          return (
            <Switch
              size="small"
              checked={params.row.is_active === 1 || params.row.is_active === "1"}
              onChange={() => handleToggleActive(params.row)}
              color="primary"
            />
          );
        }
        return "-";
      },
    },
    {
      field: "menu",
      headerName: <FontAwesomeIcon icon={faEllipsisV} style={{ marginRight: "15px" }} />,
      sortable: false,
      filterable: false,
      headerAlign: "right",
      align: "right",
      disableColumnMenu: true,
      width: 120,
      renderCell: (params) => (
        <div className="d-flex justify-content-center dotHoverempicon">
          <FontAwesomeIcon icon={faEllipsisV} />
          <div className="expandDiv">
            <Tooltip title="Edit">
              <button
                type="button"
                className="myActionBtn m-1"
                onClick={() => handleEdit(params.row)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  className="bi bi-pencil-square"
                  viewBox="0 0 16 16"
                >
                  <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                  <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z" />
                </svg>
              </button>
            </Tooltip>
            <Tooltip title="Delete">
              <button
                type="button"
                className="myActionBtn m-1"
                onClick={() => deleteAction(params.row)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  className="bi bi-trash3"
                  viewBox="0 0 16 16"
                >
                  <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z" />
                </svg>
              </button>
            </Tooltip>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="my-3 d-flex align-items-end justify-content-end">
        <div className="d-flex align-items-center">
          <h6 className="m-0 ps-1">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAdd}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                className="bi bi-plus-circle"
                viewBox="0 0 16 16"
              >
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
              </svg>
              &nbsp; Add Service
            </button>
          </h6>
          <Button
            variant="btn btn-warning mx-1"
            style={{ width: "75px", height: "40px", fontSize: "14px", borderRadius: "20px" }}
            onClick={redirectBack}
          >
            BACK
          </Button>
        </div>
      </div>

      <div className="addUser myBorder bg-white rounded p-3">
        <div className="tableMenuHover mt-4" style={{ height: "85vh", width: "100%" }}>
          <PersistentDataGrid
            rows={list}
            columns={columns}
            getRowId={(params) => params.id}
            className="rounded"
            style={{ fontFamily: "Poppins", padding: 5, backgroundColor: "white" }}
            pageSizeOptions={[10, 15, 25, 50]}
            rowsPerPageOptions={[10, 20, 25, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            components={{
              Toolbar: GridToolbar,
              NoRowsOverlay: () => (
                <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span>No service list entries</span>
                </div>
              ),
            }}
            componentsProps={{
              toolbar: {
                style: { fontFamily: "Poppins", alignSelf: "end" },
              },
            }}
          />
        </div>
      </div>

      <AlertDeleteModal
        showModal={displayConfirmationModal}
        confirmModal={submitDelete}
        hideModal={hideConfirmationModal}
        type="servicelist_delete"
        id={deleteId}
        message={deleteMessage}
      />

      <Modal show={modalShow} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editId ? "Edit Service" : "Add Service"}</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <div className="mb-3">
              <label className="col-form-label">Name</label>
              <input
                type="text"
                className="form-control"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. 50 Hours / 90 Days"
                required
              />
            </div>
            <div className="mb-3">
              <label className="col-form-label">Description</label>
              <input
                type="text"
                className="form-control"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="e.g. First Service"
              />
            </div>
            <div className="mb-3">
              <label className="col-form-label">Hours threshold</label>
              <input
                type="number"
                className="form-control"
                name="hours_threshold"
                value={formData.hours_threshold}
                onChange={handleChange}
                placeholder="e.g. 50"
                min={0}
                required
              />
            </div>
            <div className="mb-3">
              <label className="col-form-label">Days threshold (optional)</label>
              <input
                type="number"
                className="form-control"
                name="days_threshold"
                value={formData.days_threshold}
                onChange={handleChange}
                placeholder="e.g. 90 or leave empty"
                min={0}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Close
            </Button>
            <Button variant="primary" type="submit">
              {editId ? "Save" : "Add"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
}
